// Nombre de pantalla estable a partir del pathname, para analítica.
//
// Las rutas de la app cuelgan de `/[agency]/...` (el primer segmento es el slug
// de agencia: lincoln, carmendeareco, …) y muchas terminan en un id/uuid
// (`/lincoln/casefiles/<uuid>`). Sin normalizar, PostHog ve un `$pathname`
// distinto por agencia y por caso → imposible agrupar "cuánto se usa la pantalla
// de detalle de caso". Esta función colapsa eso a un nombre canónico:
//   /lincoln/casefiles/<uuid>  → "casefiles/:id"
//   /carmendeareco/dashboard   → "dashboard"
//   /lincoln/casefiles/new     → "casefiles/new"
//   /superadmin                → "superadmin"
//   /login                     → "login"
//
// Se aplica en `before_send` (instrumentation-client.ts) a cada evento que trae
// `$pathname`, agregando la propiedad `screen`. Pura y testeada (lógica de
// agrupación de rutas = fácil de romper en silencio).

// Primer segmento que NO es un slug de agencia: rutas top-level (fuera del
// grupo `(app)/[agency]`) + los prefijos de infra que nunca queremos como
// "agencia". Si el primer segmento cae acá, se conserva; si no, es el slug de
// agencia y se descarta.
const TOP_LEVEL_SEGMENTS = new Set([
  "login",
  "actividad",
  "change-password",
  "comunicacion",
  "instalar",
  "no-tenant",
  "platform-costs",
  "select-agency",
  "superadmin",
  "tenant-admin",
  "sos",
  "api",
  "ingest",
]);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isIdSegment(seg: string): boolean {
  return UUID_RE.test(seg) || /^\d+$/.test(seg);
}

export function screenNameFromPath(pathname: string | null | undefined): string {
  if (!pathname) return "home";
  // Sacamos query/hash y segmentos vacíos.
  const path = pathname.split(/[?#]/)[0];
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return "home";

  // Si el primer segmento no es una ruta top-level conocida, es el slug de
  // agencia → lo descartamos para que todas las agencias agrupen igual.
  let rest = segments;
  if (!TOP_LEVEL_SEGMENTS.has(segments[0])) {
    rest = segments.slice(1);
    if (rest.length === 0) return "agency_home";
  }

  const normalized = rest.map((seg) => (isIdSegment(seg) ? ":id" : seg));
  return normalized.join("/") || "home";
}
